import React from 'react';
import cn from 'classnames';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faPlay } from '@fortawesome/pro-regular-svg-icons';
import { faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons';
import ShopmyImage from '../General/ShopmyImage';
import { getPrettyNumber } from '../../helpers/formatting';
import { getCreatorProfileUrl } from '../../helpers/helpers';
import {
  METHOD_META,
  bandOf,
  getPercentDisplay,
  getPlatformDisplay,
  getCreatorTierDisplay,
  getDisplayProduct,
  getQualityScoreBadge,
  getReviewMeta,
  isRunRow,
  isUnmatchedRow,
  isWatchablePost
} from '../../helpers/product_mention_helpers';
import './ProductMentionsGrid.scss';

const ProductMentionsGrid = props => {
  const { rows, onOpen, onCheck, onConfirm, onRetry, isChecked, canSelect, showStatus } = props;

  return (
    <div className='product-mentions-grid'>
      {rows.map(row => {
        const product = getDisplayProduct(row);
        const method = METHOD_META[row.method] || METHOD_META.text;
        const review = getReviewMeta(row);
        const checked = isChecked(row);
        const run = isRunRow(row);
        const grade = !run && getQualityScoreBadge(row);
        const unmatched = isUnmatchedRow(row);
        const noMatch = run && !Number(row.matchCount);
        const band = bandOf(row.confidence);
        const tierDisplay = getCreatorTierDisplay(row.creatorTier);
        const cardActions = unmatched
          ? [
              { display: 'Try again', className: 'retry', onClick: () => onRetry(row) },
              { display: 'Tag', className: 'override', onClick: () => onOpen(row) }
            ]
          : run
            ? [{ display: 'View', className: 'override', onClick: () => onOpen(row) }]
          : [
              { display: 'Confirm', className: 'confirm', onClick: () => onConfirm(row) },
              { display: 'Edit', className: 'override', onClick: () => onOpen(row) }
            ];
        return (
          <div key={row.id} className={cn('pm-card', { checked })}>
            <div className='pm-card-media' onClick={() => onOpen(row)}>
              {row.postImage ? <ShopmyImage className='pm-card-image' source={row.postImage} alt='' loading='lazy' decoding='async' /> : <div className='pm-card-image placeholder' />}
              {isWatchablePost(row) && (
                <span className='pm-card-play' aria-hidden>
                  <FontAwesomeIcon icon={faPlay} />
                </span>
              )}
              <div className='pm-card-chips'>
                <span className='pm-card-chip format'>{getPlatformDisplay(row.platform)}</span>
                <span className={cn('pm-card-chip', noMatch ? 'none' : band)}>{noMatch ? 'No match' : getPercentDisplay(row.confidence)}</span>
              </div>
              {canSelect && (
                <div
                  className='pm-card-check'
                  onClick={e => {
                    e.stopPropagation();
                    onCheck(row);
                  }}
                >
                  <FontAwesomeIcon icon={faCheck} />
                </div>
              )}
              <div className='pm-card-overlay'>
                <div className='pm-card-meta'>
                  {row.postViews != null && `${getPrettyNumber(row.postViews)} views · `}
                  {moment(row.contentPublishedAt || row.createdAt).fromNow(true)}
                  {tierDisplay && ` · ${tierDisplay}`}
                </div>
                {row.creatorUsername && (
                  <a
                    className='pm-card-handle'
                    href={getCreatorProfileUrl(row.creatorUsername)}
                    target='_blank'
                    rel='noopener noreferrer'
                    onClick={e => e.stopPropagation()}
                  >
                    @{row.creatorUsername}
                  </a>
                )}
              </div>
            </div>

            <div className='pm-card-body'>
              <div className='pm-card-tagged'>
                {product.image ? <ShopmyImage className='pm-card-product-thumb' source={product.image} alt='' loading='lazy' decoding='async' /> : <div className='pm-card-product-thumb placeholder' />}
                <div className='pm-card-tagged-text'>
                  <div className='pm-card-eyebrow'>{run ? `${row.matchCount || 0} found` : 'Tagged as'}</div>
                  <div className={cn('pm-card-product', { overridden: product.isOverridden })}>{product.title}</div>
                  <div className='pm-card-brand'>
                    {product.productId
                      ? `${product.brandName || '—'} · #${product.productId}${row.matchCount > 1 ? ` · +${row.matchCount - 1}` : ''}`
                      : '—'}
                  </div>
                </div>
              </div>
              <div className='pm-card-badges'>
                {!noMatch && (
                  <span className='pm-method-chip'>
                    <FontAwesomeIcon icon={method.icon} />
                    {method.display}
                  </span>
                )}
                {showStatus && <span className={cn('pm-status-badge', review.className)}>{review.display}</span>}
                {grade && (
                  <span className={cn('pm-grade-badge', { model: grade.isModel })} title={grade.title}>
                    {grade.label}
                    {grade.isModel && <span className='pm-grade-source'>model</span>}
                  </span>
                )}
                {!run && Boolean(row.isExemplar) && (
                  <span className='pm-exemplar-badge' title='Strong example'>
                    <FontAwesomeIcon icon={faStarSolid} />
                  </span>
                )}
              </div>
              <div className='pm-card-actions'>
                {cardActions.map(action => (
                  <button type='button' key={action.display} className={action.className} onClick={action.onClick}>
                    {action.display}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProductMentionsGrid;
